import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Plus, Minus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SearchInput } from '@/components/shared/SearchInput';
import { Pagination } from '@/components/shared/Pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useGetWallets,
  useGetWalletTransactions,
  useAdjustWalletBalance,
} from '@/services/api/endpoints/WalletService';
import type { AdminWallet, WalletTransactionItem } from '@/services/api/endpoints/WalletService';
import { useDebounce } from '@/hooks/useDebounce';
import { formatDate, formatAmount } from '@/lib/utils';
import { toast } from 'sonner';

function formatBalance(amount: number): string {
  return formatAmount(amount);
}

const TX_TYPE_STYLES: Record<string, string> = {
  deposit: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  payment: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  refund: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  adjustment: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  withdrawal: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

export function WalletsPage() {
  const { t } = useTranslation();

  // List state
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  // Detail modal state
  const [selectedWallet, setSelectedWallet] = useState<AdminWallet | null>(null);
  const [txPage, setTxPage] = useState(1);

  // Adjust modal state
  const [adjustWallet, setAdjustWallet] = useState<AdminWallet | null>(null);
  const [adjustType, setAdjustType] = useState<'credit' | 'debit'>('credit');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');

  const { data, isLoading } = useGetWallets(page, 20, debouncedSearch, 'createdAt', 'desc');
  const { data: txData, isLoading: txLoading } = useGetWalletTransactions(
    selectedWallet?.userId || '',
    txPage,
    15
  );
  const adjustMutation = useAdjustWalletBalance();

  const openTransactions = (wallet: AdminWallet) => {
    setSelectedWallet(wallet);
    setTxPage(1);
  };

  const openAdjust = (wallet: AdminWallet, type: 'credit' | 'debit') => {
    setAdjustWallet(wallet);
    setAdjustType(type);
    setAdjustAmount('');
    setAdjustReason('');
  };

  const handleAdjust = async () => {
    if (!adjustWallet || !adjustAmount || !adjustReason.trim()) return;
    const amountRsd = Math.round(parseFloat(adjustAmount));
    if (isNaN(amountRsd) || amountRsd <= 0) {
      toast.error(t('wallets.invalidAmount'));
      return;
    }

    try {
      await adjustMutation.mutateAsync({
        userId: adjustWallet.userId,
        companyId: adjustWallet.companyId,
        amount: amountRsd,
        type: adjustType,
        reason: adjustReason.trim(),
      });
      toast.success(t('wallets.adjustSuccess'));
      setAdjustWallet(null);
    } catch {
      toast.error(t('wallets.adjustError'));
    }
  };

  const getUserDisplayName = (wallet: AdminWallet) => {
    const u = wallet.user;
    if (!u) return '-';
    if (u.firstName || u.lastName) return `${u.firstName || ''} ${u.lastName || ''}`.trim();
    return u.email;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('wallets.title')}</h1>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="max-w-sm flex-1">
          <SearchInput value={search} onChange={setSearch} />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('wallets.user')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('wallets.email')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('wallets.company')}</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">{t('wallets.balance')}</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">{t('wallets.reserved')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.status')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((wallet) => (
                      <tr key={wallet.id} className="border-b hover:bg-muted/30 transition-colors">
                        <td className="p-4 text-sm font-medium">{getUserDisplayName(wallet)}</td>
                        <td className="p-4 text-sm text-muted-foreground">{wallet.user?.email || '-'}</td>
                        <td className="p-4 text-sm text-muted-foreground">{wallet.user?.company?.name || '-'}</td>
                        <td className="p-4 text-sm text-right font-medium">{formatBalance(wallet.balance)}</td>
                        <td className="p-4 text-sm text-right text-muted-foreground">
                          {wallet.reservedAmount > 0 ? formatBalance(wallet.reservedAmount) : '-'}
                        </td>
                        <td className="p-4 text-center">
                          <Badge variant={wallet.status === 'active' ? 'default' : 'secondary'}>
                            {wallet.status === 'active' ? t('common.active') : t('common.inactive')}
                          </Badge>
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openTransactions(wallet)}
                              title={t('wallets.viewTransactions')}
                            >
                              <CreditCard className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openAdjust(wallet, 'credit')}
                              className="text-green-600 hover:text-green-700"
                              title={t('wallets.credit')}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openAdjust(wallet, 'debit')}
                              className="text-red-600 hover:text-red-700"
                              title={t('wallets.debit')}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {data?.items.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-muted-foreground">
                          {t('common.noResults')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {data?.items.map((wallet) => (
                  <div key={wallet.id} className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{getUserDisplayName(wallet)}</span>
                      <span className="text-sm font-medium">{formatBalance(wallet.balance)}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">{wallet.user?.email || '-'}</div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{wallet.user?.company?.name || '-'}</span>
                      <Badge variant={wallet.status === 'active' ? 'default' : 'secondary'}>
                        {wallet.status === 'active' ? t('common.active') : t('common.inactive')}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => openTransactions(wallet)}
                        title={t('wallets.viewTransactions')}
                      >
                        <CreditCard className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-green-600 hover:text-green-700"
                        onClick={() => openAdjust(wallet, 'credit')}
                        title={t('wallets.credit')}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-red-600 hover:text-red-700"
                        onClick={() => openAdjust(wallet, 'debit')}
                        title={t('wallets.debit')}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {data?.items.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    {t('common.noResults')}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {data?.pagination && (
        <Pagination
          page={page}
          totalPages={data.pagination.totalPages}
          onPageChange={setPage}
        />
      )}

      {/* Transaction History Dialog */}
      <Dialog open={!!selectedWallet} onOpenChange={() => setSelectedWallet(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t('wallets.transactionsFor')} {selectedWallet?.user?.email}
            </DialogTitle>
          </DialogHeader>
          <div className="mb-2 text-sm text-muted-foreground">
            {t('wallets.currentBalance')}: <span className="font-medium text-foreground">{selectedWallet ? formatBalance(selectedWallet.balance) : '-'}</span>
          </div>
          {txLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <table className="hidden md:table w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium text-muted-foreground">{t('wallets.txDate')}</th>
                    <th className="text-center p-3 font-medium text-muted-foreground">{t('wallets.txType')}</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">{t('wallets.txAmount')}</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">{t('wallets.txBalanceAfter')}</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">{t('wallets.txDescription')}</th>
                  </tr>
                </thead>
                <tbody>
                  {txData?.items.map((tx: WalletTransactionItem) => (
                    <tr key={tx.id} className="border-b">
                      <td className="p-3">{formatDate(tx.createdAt)}</td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className={TX_TYPE_STYLES[tx.type] || ''}>
                          {t(`wallets.txTypes.${tx.type}`, tx.type)}
                        </Badge>
                      </td>
                      <td className={`p-3 text-right font-medium ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.amount >= 0 ? '+' : ''}{formatBalance(tx.amount)}
                      </td>
                      <td className="p-3 text-right text-muted-foreground">{formatBalance(tx.balanceAfter)}</td>
                      <td className="p-3 text-muted-foreground max-w-[200px] truncate">{tx.description || '-'}</td>
                    </tr>
                  ))}
                  {txData?.items.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-6 text-center text-muted-foreground">
                        {t('common.noResults')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {txData?.items.map((tx: WalletTransactionItem) => (
                  <div key={tx.id} className="p-3 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{formatDate(tx.createdAt)}</span>
                      <Badge variant="outline" className={TX_TYPE_STYLES[tx.type] || ''}>
                        {t(`wallets.txTypes.${tx.type}`, tx.type)}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.amount >= 0 ? '+' : ''}{formatBalance(tx.amount)}
                      </span>
                      <span className="text-sm text-muted-foreground">{t('wallets.txBalanceAfter')}: {formatBalance(tx.balanceAfter)}</span>
                    </div>
                    {tx.description && (
                      <div className="text-xs text-muted-foreground truncate">{tx.description}</div>
                    )}
                  </div>
                ))}
                {txData?.items.length === 0 && (
                  <div className="p-6 text-center text-muted-foreground">
                    {t('common.noResults')}
                  </div>
                )}
              </div>
            </>
          )}
          {txData?.pagination && txData.pagination.totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                page={txPage}
                totalPages={txData.pagination.totalPages}
                onPageChange={setTxPage}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust Balance Dialog */}
      <Dialog open={!!adjustWallet} onOpenChange={() => setAdjustWallet(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {adjustType === 'credit' ? t('wallets.creditTitle') : t('wallets.debitTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-sm text-muted-foreground">
              {t('wallets.adjustingFor')} <span className="font-medium text-foreground">{adjustWallet?.user?.email}</span>
            </div>
            <div className="text-sm text-muted-foreground">
              {t('wallets.currentBalance')}: <span className="font-medium text-foreground">{adjustWallet ? formatBalance(adjustWallet.balance) : '-'}</span>
            </div>
            <div className="space-y-2">
              <Label>{t('wallets.amount')} (RSD)</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={adjustAmount}
                onChange={e => setAdjustAmount(e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('wallets.reason')}</Label>
              <Input
                value={adjustReason}
                onChange={e => setAdjustReason(e.target.value)}
                placeholder={t('wallets.reasonPlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustWallet(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleAdjust}
              disabled={adjustMutation.isPending || !adjustAmount || !adjustReason.trim()}
              variant={adjustType === 'credit' ? 'default' : 'destructive'}
            >
              {adjustMutation.isPending ? t('common.saving') : (adjustType === 'credit' ? t('wallets.credit') : t('wallets.debit'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
